import React, { useEffect, useRef, useState } from "react";
import ReactDOM from 'react-dom';
import * as api from "../../../../backend/request";
import { toast } from "react-toastify";
import PageLoader from "../../../shared/PageLoader";
import { usePath, useHistory, navigate } from "raviger";
import { DEFAULT_PAGE_SIZE } from "../../../common/Table";
import Location from "../../../common/LocationV2";
import AuditSelect from '../../../common/AuditSelect';
import { useGetAllLocationsAudits, useGetUserCompanies } from "../../../../backend/query";
import ModuleNotSubscribed from "../../../common/ModuleNotSubscribed";
import { useAuditReport } from "../../../../backend/exports";
import { downloadFileContent } from "../../../../utils/common";
import {
  AUDIT_STATUS_LABELS,
  AUDIT_STATUS as LOCATION_AUDIT_STATUS,
} from "../../../../utils/auditStateMachine";
import { ERROR_MESSAGES } from "../../../../utils/constants";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { TOOLTIP_DELAY } from "../../../common/Constants";
import { hasUserAccess } from "../../../../backend/auth";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";
import LocationDropAuditModal from "./LocationDropAuditModal";
import LocationAuditBulkDownloadModal from "./LocationAuditBulkDownloadModal";
import "./AuditCommon.css";
import "./AuditListV2.css";

// ── Constants ──────────────────────────────────────────────────────────────

const INCLUDE_LIST = [
  "company",
  "location",
  "associate_company",
  "preliminary_report",
  "final_report",
];

const AuditTypeFilter: Record<string, any> = {};

const AUDIT_STATUS_FILTER_OPTIONS = [
  {
    value: "scheduled",
    label: "Scheduled Audits",
    tagClass: "tag-scheduled",
    statuses: [LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_PENDING],
  },
  {
    value: "in_progress",
    label: "Preliminary In Progress",
    tagClass: "tag-inprogress",
    statuses: [
      LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_SUBMITTED,
      LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_REPORT_UPLOADING,
      LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_REPORT_FAILED,
      LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_EDITING,
    ],
  },
  {
    value: "preliminary_completed",
    label: "Preliminary Audit Completed",
    tagClass: "tag-preliminary",
    statuses: [
      LOCATION_AUDIT_STATUS.FINAL_AUDIT_PENDING,
      LOCATION_AUDIT_STATUS.FINAL_AUDIT_EDITING,
      LOCATION_AUDIT_STATUS.FINAL_AUDIT_SUBMITTED,
      LOCATION_AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADING,
      LOCATION_AUDIT_STATUS.FINAL_AUDIT_REPORT_FAILED,
    ],
  },
  {
    value: "final_completed",
    label: "Final Audit Completed",
    tagClass: "tag-final",
    statuses: [
      LOCATION_AUDIT_STATUS.FINAL_AUDIT_PUBLISHED,
      LOCATION_AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADED,
    ],
  },
  {
    value: "dropped",
    label: "Dropped Audit",
    tagClass: "tag-dropped",
    statuses: [LOCATION_AUDIT_STATUS.DROPPED_AUDIT],
  },
];

const TIME_RANGE_OPTIONS = [
  { value: "full_year",  label: "Full Year" },
  { value: "this_month", label: "This Month" },
  { value: "q1",        label: "Quarter 1 (Jan–Mar)" },
  { value: "q2",        label: "Quarter 2 (Apr–Jun)" },
  { value: "q3",        label: "Quarter 3 (Jul–Sep)" },
  { value: "q4",        label: "Quarter 4 (Oct–Dec)" },
  { value: "custom",    label: "Custom Range" },
];

const MONTH_OPTIONS = [
  { value: 1,  label: "January" },   { value: 2,  label: "February" },
  { value: 3,  label: "March" },     { value: 4,  label: "April" },
  { value: 5,  label: "May" },       { value: 6,  label: "June" },
  { value: 7,  label: "July" },      { value: 8,  label: "August" },
  { value: 9,  label: "September" }, { value: 10, label: "October" },
  { value: 11, label: "November" },  { value: 12, label: "December" },
];

const _currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 10 }, (_, i) => _currentYear - i);

function getAdvanceSearch(state: any) {
  const filters: any = {};
  if ((state || {}).fromDate) {
    filters["from_date_gteq"] = new Date(state.fromDate).toISOString();
    filters["to_date"] = new Date(state.toDate).toISOString();
  }
  if ((state || {}).audit_type) {
    filters["audit_type"] = state.auditType;
  }
  if ((state || {}).dueDateBefore) {
    filters["todos_due_date_lteq"] = state.dueDateBefore;
  }
  return filters;
}

function getStatusPillClass(status: string): string {
  if (!status) return "";
  if (([LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_PENDING] as string[]).includes(status))
    return "pill-scheduled";
  if (
    ([
      LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_SUBMITTED,
      LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_REPORT_UPLOADING,
      LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_REPORT_FAILED,
      LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_EDITING,
    ] as string[]).includes(status)
  )
    return "pill-inprogress";
  if (
    ([
      LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_FINALIZED,
      LOCATION_AUDIT_STATUS.FINAL_AUDIT_PENDING,
      LOCATION_AUDIT_STATUS.FINAL_AUDIT_EDITING,
      LOCATION_AUDIT_STATUS.FINAL_AUDIT_SUBMITTED,
      LOCATION_AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADING,
      LOCATION_AUDIT_STATUS.FINAL_AUDIT_REPORT_FAILED,
    ] as string[]).includes(status)
  )
    return "pill-preliminary";
  if (
    ([
      LOCATION_AUDIT_STATUS.FINAL_AUDIT_PUBLISHED,
      LOCATION_AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADED,
    ] as string[]).includes(status)
  )
    return "pill-final";
  if (([LOCATION_AUDIT_STATUS.DROPPED_AUDIT] as string[]).includes(status))
    return "pill-dropped";
  return "";
}

function getPageNumbers(current: number, last: number): number[] {
  const delta = 2;
  const start = Math.max(1, current - delta);
  const end = Math.min(last, current + delta);
  const range: number[] = [];
  for (let i = start; i <= end; i++) range.push(i);
  return range;
}

function TextCell({ className, value }: { className: string; value: string }) {
  const text = value || '—';
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{text}</Tooltip>}
      delay={{ show: TOOLTIP_DELAY } as any}
    >
      <td className={className}>{text}</td>
    </OverlayTrigger>
  );
}

function InfoTooltip({ html }: { html: string }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLSpanElement>(null);
  if (!html) return null;

  function handleMouseEnter() {
    if (ref && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({ top: rect.top, left: rect.left + rect.width / 2 });
    }
    setShow(true);
  }

  return (
    <span
      ref={ref}
      className="al-info-tooltip-wrap"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShow(false)}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
      {show && ReactDOM.createPortal(
        <div
          className="al-info-tooltip-box"
          style={{ position: 'fixed', top: pos.top, left: pos.left, transform: 'translateX(-50%) translateY(calc(-100% - 6px))' }}
          dangerouslySetInnerHTML={{ __html: html }}
        />,
        document.body
      )}
    </span>
  );
}

function AuditListV2() {
  const { state }: any = useHistory();
  const path: any = usePath();
  const [fromDashboard] = useState(path.includes("/dashboard/activities"));
  const currentYear = new Date().getFullYear();

  const [submitting, setSubmitting] = useState(false);
  const [params, setParams] = useState<any>({ page: 1, per: DEFAULT_PAGE_SIZE });
  const [locationFilters, setLocationFilter] = useState<any>(
    state && state.initialFilters ? state.initialFilters : undefined
  );
  const lfRef = useRef<any>();
  lfRef.current = locationFilters;
  const [advaceSearchFilters] = useState(getAdvanceSearch(state));
  const afRef = useRef<any>();
  afRef.current = advaceSearchFilters;
  const [payload, setPayload] = useState<any>();
  const payloadRef = useRef<any>();
  payloadRef.current = payload;
  const locationFilterTimerRef = useRef<any>(null);
  const [previousFilters, setPreviousFilters] = useState<any>(state || {});
  const previousFiltersRef = useRef<any>();
  previousFiltersRef.current = previousFilters;
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [dropAudit, setDropAudit] = useState<any>(null);
  const [enableBulkDownloadModal, setEnableBulkDownloadModal] = useState<boolean>(false);
  const [filterKey, setFilterKey] = useState(0);


  const [timeRange, setTimeRange] = useState(state && state.timeRange ? state.timeRange : "full_year");
  const [timeRangeYear, setTimeRangeYear] = useState<number | "all">(
    state && state.year ? (state.year === "all" ? "all" : Number(state.year)) : currentYear
  );
  const [timeRangeMonths, setTimeRangeMonths] = useState<number[]>(
    state && Array.isArray(state.months) ? state.months : []
  );
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const monthPickerRef = useRef<HTMLDivElement>(null);
  const timeRangeRef = useRef(timeRange);
  timeRangeRef.current = timeRange;
  const timeRangeYearRef = useRef<number | "all">(timeRangeYear);
  timeRangeYearRef.current = timeRangeYear;
  const timeRangeMonthsRef = useRef<number[]>(timeRangeMonths);
  timeRangeMonthsRef.current = timeRangeMonths;
  const [auditStatusFilter, setAuditStatusFilter] = useState<any>(() => {
    if (state && state.auditStatuses && Array.isArray(state.auditStatuses)) {
      return state.auditStatuses.reduce((acc: any, s: string) => { acc[s] = true; return acc; }, {});
    }
    if (state && state.auditStatus) return { [state.auditStatus]: true };
    return {};
  });
  const auditStatusRef = useRef<any>({});
  auditStatusRef.current = auditStatusFilter;

  const showYear = ["full_year", "q1", "q2", "q3", "q4", "custom"].includes(timeRange);
  const showMonth = timeRange === "custom";

  const { activities, total, isFetching, refetch } = useGetAllLocationsAudits(
    payload,
    Boolean(hasFilters(null, "company_id_eq") || hasFilters(null, "company_id_in"))
  );

  const { userCompanies: accessCompanies, isFetching: checkingAccess } = useGetUserCompanies(
    { filters: { is_active_eq: true } }
  );

  const { auditReport, exporting } = useAuditReport(
    (response: any) => {
      if (response) {
        const filename =
          selectedReport && selectedReport.name
            ? selectedReport.name.charAt(0).toUpperCase() + selectedReport.name.slice(1)
            : "audit_report";
        downloadFileContent({
          name: filename,
          type: response.headers["content-type"],
          content: response.data,
        });
      }
    },
    () => {
      toast.error(ERROR_MESSAGES.DEFAULT);
    }
  );

  const canBulkDownload =
    hasUserAccess(USER_PRIVILEGES.REVIEWER_ACTIVITIES_DOWNLOAD_BULK_REPORT) ||
    hasUserAccess(USER_PRIVILEGES.SUBMITTER_ACTIVITIES_DOWNLOAD_BULK_REPORT);

  const currentPage = (params && params.page) || 1;
  const perPage = (params && params.per) || DEFAULT_PAGE_SIZE;
  const totalRecords = total || 0;
  const totalPages = Math.ceil(totalRecords / perPage) || 1;
  const showingFrom = totalRecords ? (currentPage - 1) * perPage + 1 : 0;
  const showingTo = Math.min(currentPage * perPage, totalRecords);

  function hasFilters(ref: any, field = "company_id_eq") {
    const _filters: any =
      (ref
        ? ref.current
        : ({ ...(payloadRef.current || {}) } as any)["filters"]) || {};
    return _filters[field];
  }

  function getStatusFilters() {
    const selected = auditStatusRef.current || {};
    const selectedStatuses = AUDIT_STATUS_FILTER_OPTIONS.filter(
      (opt) => selected[opt.value]
    ).flatMap((opt) => opt.statuses);
    return selectedStatuses.length > 0
      ? { status_in: selectedStatuses }
      : { status_not_eq: LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_CREATED };
  }

  const QUARTER_MONTH_NAMES: Record<string, string[]> = {
    q1: ['January','February','March'],
    q2: ['April','May','June'],
    q3: ['July','August','September'],
    q4: ['October','November','December'],
  };

  function getTimeFilters() {
    const tr = timeRangeRef.current;
    const yr = timeRangeYearRef.current;
    const months = timeRangeMonthsRef.current;
    const monthNames = MONTH_OPTIONS.map(m => m.label);
    if (tr === 'this_month') {
      return { year_eq: String(new Date().getFullYear()), month_eq: monthNames[new Date().getMonth()] };
    }
    const yearFilter = yr !== "all" ? { year_eq: String(yr) } : {};
    if (tr === 'full_year') return yearFilter;
    if (QUARTER_MONTH_NAMES[tr]) return { ...yearFilter, month_in: QUARTER_MONTH_NAMES[tr] };
    if (tr === 'custom') {
      if (months.length > 0) return { ...yearFilter, month_in: months.map(m => monthNames[m - 1]) };
      return yearFilter; // no months selected = all months for the year
    }
    return {};
  }

  function buildPayload(page: number, locationF?: any, per?: number) {
    // When todos_due_date_lteq is active, still scope by year but skip month/range filters
    const timeFilters = (afRef.current || {}).todos_due_date_lteq
      ? (timeRangeYearRef.current !== "all" ? { year_eq: String(timeRangeYearRef.current) } : {})
      : getTimeFilters();
    return {
      page,
      per: per !== undefined ? per : (params.per || DEFAULT_PAGE_SIZE),
      include: INCLUDE_LIST,
      includes: INCLUDE_LIST,
      filters: {
        ...(locationF !== undefined ? locationF : (lfRef.current || {})),
        ...(afRef.current || {}),
        ...getStatusFilters(),
        ...timeFilters,
        ...AuditTypeFilter,
      },
    };
  }

  const hasActiveFilters =
    timeRange !== 'full_year' ||
    (timeRangeYear !== currentYear && timeRangeYear !== 'all') ||
    timeRangeMonths.length > 0 ||
    Object.values(auditStatusFilter || {}).some(Boolean) ||
    !!(locationFilters && (
      locationFilters.company_id_eq ||
      locationFilters.associate_company_id_eq ||
      locationFilters.location_id_eq
    ));

  function handleResetFilters() {
    setTimeRange('full_year');
    setTimeRangeYear(currentYear);
    setTimeRangeMonths([]);
    setMonthPickerOpen(false);
    setAuditStatusFilter({});
    setLocationFilter(undefined);
    setPreviousFilters({});
    setFilterKey(k => k + 1);
    setParams({ page: 1, per: DEFAULT_PAGE_SIZE });
    setPayload({
      page: 1,
      per: DEFAULT_PAGE_SIZE,
      include: INCLUDE_LIST,
      includes: INCLUDE_LIST,
      filters: {
        ...AuditTypeFilter,
        year_eq: String(currentYear),
      },
    });
  }

  function onLocationChange(event: any, filter: any = {}) {
    setLocationFilter(event);
    if (Object.keys(filter).length > 0) {
      setPreviousFilters((prev: any) => ({ ...prev, ...filter }));
    }
  }

  async function downloadReport(audit: any) {
    if (!audit || !audit.id) return;
    setSubmitting(true);
    try {
      let auditData: any;
      if (audit.preliminary_report || audit.final_report) {
        auditData = audit;
      } else {
        const response = await api.get(`/api/v1/location_audits/${audit.id}`, {
          include: ["preliminary_report", "final_report"],
          includes: ["preliminary_report", "final_report"],
        });
        auditData = response.data;
      }
      let reportData: any;
      if (
        [
          LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_FINALIZED,
          LOCATION_AUDIT_STATUS.FINAL_AUDIT_PENDING,
          LOCATION_AUDIT_STATUS.FINAL_AUDIT_SUBMITTED,
          LOCATION_AUDIT_STATUS.FINAL_AUDIT_EDITING,
        ].includes(audit.status)
      ) {
        reportData = auditData.preliminary_report;
      } else if (
        [
          LOCATION_AUDIT_STATUS.FINAL_AUDIT_PUBLISHED,
          LOCATION_AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADED,
        ].includes(audit.status)
      ) {
        reportData = auditData.final_report;
      }
      setSelectedReport(reportData);
      if (!reportData) {
        toast.warn("No report available");
        setSubmitting(false);
        return;
      }
      const { s3_region, s3_key, s3_bucket } = reportData;
      if (s3_region && s3_key && s3_bucket) {
        auditReport({ s3_region, s3_key, s3_bucket });
      } else {
        toast.warn("No report available");
      }
    } catch (error) {
      console.error("Error fetching audit:", error);
    }
    setSubmitting(false);
  }

  function rowClick(audit: any) {
    if (audit && audit.id) {
      const auditStatuses = Object.keys(auditStatusFilter).filter((k) => auditStatusFilter[k]);
      navigate(`/location-audits/${audit.id}`, {
        state: {
          ...(previousFiltersRef.current || {}),
          initialFilters: lfRef.current,
          timeRange: timeRangeRef.current,
          year: timeRangeYearRef.current,
          months: timeRangeMonthsRef.current,
          auditStatuses,
        },
      });
    }
  }

  function handlePageNav(newPage: number) {
    setParams((prev: any) => ({ ...prev, page: newPage }));
    setPayload(buildPayload(newPage));
  }

  function handlePerPageChange(newPer: number) {
    setParams((prev: any) => ({ ...prev, page: 1, per: newPer }));
    setPayload(buildPayload(1, undefined, newPer));
  }

  useEffect(() => {
    setPayload(buildPayload(1));
  }, []);

  useEffect(() => {
    if (!monthPickerOpen) return;
    function handleOutside(e: MouseEvent) {
      if (monthPickerRef.current && !monthPickerRef.current.contains(e.target as Node)) {
        setMonthPickerOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [monthPickerOpen]);

  useEffect(() => {
    if (locationFilters !== undefined) {
      // Debounce: the Location component fires onChange up to 3 times while
      // restoring previous filters (company → assoc → location cascade). Without
      // debouncing, each intermediate value triggers a separate API call and
      // repeatedly locks the page via PageLoader's body.overflow:hidden.
      if (locationFilterTimerRef.current) clearTimeout(locationFilterTimerRef.current);
      locationFilterTimerRef.current = setTimeout(() => {
        setParams((prev: any) => ({ ...prev, page: 1 }));
        setPayload(buildPayload(1, locationFilters));
      }, 150);
    }
    return () => {
      if (locationFilterTimerRef.current) clearTimeout(locationFilterTimerRef.current);
    };
  }, [locationFilters]);

  useEffect(() => {
    setParams((prev: any) => ({ ...prev, page: 1 }));
    setPayload(buildPayload(1));
  }, [auditStatusFilter]);

  useEffect(() => {
    setParams((prev: any) => ({ ...prev, page: 1 }));
    setPayload(buildPayload(1));
  }, [timeRange, timeRangeYear, timeRangeMonths]); // eslint-disable-line react-hooks/exhaustive-deps

  if (checkingAccess) {
    return <PageLoader />;
  }

  if (accessCompanies.length === 0) {
    return <ModuleNotSubscribed />;
  }

  return (
    <div className="audit-list-page la-audit-page">
      {/* ── Page header ── */}
      <div className="audit-page-header">
        <h1 className="audit-page-title">Location Audits</h1>
        <p className="audit-page-subtitle">All location-wise compliance audits with current status and actions.</p>
      </div>

      {/* ── Filter card ── */}
      <div className="audit-filter-card">
        <div className="audit-filters-row">
          {/* Company / Associate Company / Location */}
          <Location key={filterKey} onChange={onLocationChange} previousFilters={previousFilters} />

          {/* Time Range */}
          <div className="audit-filter-group">
            <label htmlFor="lal-time-range">Duration</label>
            <AuditSelect
              inputId="lal-time-range"
              options={TIME_RANGE_OPTIONS}
              value={TIME_RANGE_OPTIONS.find(opt => opt.value === timeRange) || null}
              onChange={(opt: any) => {
                if (!opt) return;
                if (timeRange === 'custom' && opt.value !== 'custom') {
                  setTimeRangeMonths([]);
                  setMonthPickerOpen(false);
                  setTimeRangeYear(currentYear);
                }
                setTimeRange(opt.value);
              }}
            />
          </div>

          {/* Conditional: Year dropdown */}
          {showYear && (
            <div className="audit-filter-group">
              <label htmlFor="lal-year">Year</label>
              <AuditSelect
                inputId="lal-year"
                options={[{ value: 'all', label: 'All' }, ...yearOptions.map(y => ({ value: String(y), label: String(y) }))]}
                value={timeRangeYear === 'all' ? { value: 'all', label: 'All' } : { value: String(timeRangeYear), label: String(timeRangeYear) }}
                onChange={(opt: any) => opt && setTimeRangeYear(opt.value === 'all' ? 'all' : Number(opt.value))}
              />
            </div>
          )}

          {/* Conditional: Multi-month picker (Custom Range only) */}
          {showMonth && (
            <div className="audit-filter-group lal-month-picker-wrap" ref={monthPickerRef}>
              <label>Months</label>
              <button
                type="button"
                className="lal-month-picker-trigger"
                onClick={() => setMonthPickerOpen(o => !o)}
              >
                <span>
                  {timeRangeMonths.length === 0
                    ? 'All Months'
                    : timeRangeMonths.length <= 2
                      ? timeRangeMonths.map(m => MONTH_OPTIONS[m - 1].label.slice(0, 3)).join(', ')
                      : `${timeRangeMonths.length} Months`}
                </span>
                {timeRangeMonths.length > 0 ? (
                  <span
                    className="lal-month-picker-clear"
                    role="button"
                    aria-label="Clear months"
                    onClick={e => { e.stopPropagation(); setTimeRangeMonths([]); setMonthPickerOpen(false); }}
                  >
                    ×
                  </span>
                ) : (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M1 3L5 7L9 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
              {monthPickerOpen && (
                <div className="lal-month-picker-popup">
                  {MONTH_OPTIONS.map(m => (
                    <button
                      key={m.value}
                      type="button"
                      className={'lal-month-cell' + (timeRangeMonths.includes(m.value) ? ' selected' : '')}
                      onClick={() => setTimeRangeMonths(prev =>
                        prev.includes(m.value) ? prev.filter(x => x !== m.value) : [...prev, m.value]
                      )}
                    >
                      {m.label.slice(0, 3)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {hasActiveFilters && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Reset Filters</Tooltip>}
              delay={{ show: 300 } as any}
            >
              <button type="button" className="audit-reset-icon-btn" onClick={handleResetFilters} aria-label="Reset Filters">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="8" cy="8" r="6.5" />
                  <line x1="5.5" y1="5.5" x2="10.5" y2="10.5" />
                  <line x1="10.5" y1="5.5" x2="5.5" y2="10.5" />
                </svg>
              </button>
            </OverlayTrigger>
          )}
        </div>

        {/* ── Status filter bar ── */}
        <div className="audit-status-bar">
          <span className="audit-status-label">Audit Status</span>
          {AUDIT_STATUS_FILTER_OPTIONS.map((opt) => {
            const isActive = !!(auditStatusFilter && auditStatusFilter[opt.value]);
            return (
              <button
                key={opt.value}
                type="button"
                className={`audit-status-tag ${opt.tagClass}${isActive ? " active-tag" : ""}`}
                onClick={() =>
                  setAuditStatusFilter((prev: any) => ({
                    ...prev,
                    [opt.value]: !prev[opt.value],
                  }))
                }
              >
                {opt.label}
              </button>
            );
          })}
          {canBulkDownload && (
            <button type="button" className="audit-bulk-btn" onClick={() => setEnableBulkDownloadModal(true)}>
              Bulk Report
            </button>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="audit-table-card">
        <div className="audit-table-scroll">
          <table className="audit-table">
            <thead>
              <tr>
                <th className="col-month">Month &amp; Year</th>
                <th className="col-company">Company</th>
                <th className="col-assoc">Associate Company</th>
                <th className="col-location">Location Name</th>
                <th className="col-status">Status</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isFetching ? (
                <tr className="audit-empty-row">
                  <td colSpan={6}>Loading...</td>
                </tr>
              ) : (!activities || activities.length === 0) ? (
                <tr className="audit-empty-row">
                  <td colSpan={6}>
                    No audits found for the selected filters.
                  </td>
                </tr>
              ) : (
                (activities || []).map((audit: any) => (
                  <tr key={audit.id} onClick={() => rowClick(audit)}>
                    <TextCell className="col-month" value={`${audit.month} (${audit.year})`} />
                    <TextCell className="col-company" value={(audit.company && audit.company.name) || "—"} />
                    <TextCell className="col-assoc" value={(audit.associate_company && audit.associate_company.name) || "—"} />
                    <TextCell className="col-location" value={(audit.location && audit.location.name) || "—"} />
                    <td className="col-status">
                      <div className="d-flex align-items-center gap-1">
                        {audit.status === LOCATION_AUDIT_STATUS.DROPPED_AUDIT && (
                          <InfoTooltip html={((audit.metadata || {}).drop_reason) || ""} />
                        )}
                        {audit.status && (
                          <span className={`audit-status-pill ${getStatusPillClass(audit.status)}`}>
                            {AUDIT_STATUS_LABELS[audit.status as keyof typeof AUDIT_STATUS_LABELS] ||
                              audit.status}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="col-actions" onClick={(e) => e.stopPropagation()}>
                      {(() => {
                        const isDropped = audit.status === LOCATION_AUDIT_STATUS.DROPPED_AUDIT;
                        const isFinalCompleted = audit.status === LOCATION_AUDIT_STATUS.FINAL_AUDIT_PUBLISHED || audit.status === LOCATION_AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADED;
                        const canDropAudit = hasUserAccess(USER_PRIVILEGES.REVIEWER_VENDOR_ACTIVITIES_DROP_AUDIT);
                        return (
                          <div className="audit-actions-cell">
                            <button
                              type="button"
                              className="audit-action-btn"
                              title="Download Report"
                              onClick={() => downloadReport(audit)}
                            >
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#2965ad" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                              </svg>
                            </button>
                            {canDropAudit && !isDropped && !isFinalCompleted ? (
                              <button
                                type="button"
                                className="audit-action-btn"
                                title="Drop Audit"
                                onClick={() => setDropAudit(audit)}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                  <rect x="2.5" y="2.5" width="11" height="11" stroke="#C00F0C"/>
                                  <path d="M11 5L5 11M5 5L11 11" stroke="#C00F0C" strokeLinecap="square" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            ) : (
                              <span style={{ width: 16, height: 16, display: 'inline-block', flexShrink: 0 }} />
                            )}
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      {/* ── Pagination ── */}
      <div className="audit-pagination">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: '#757575', fontFamily: 'Montserrat', fontWeight: 500, whiteSpace: 'nowrap' }}>
              Results per page
            </span>
            <select
              value={perPage}
              onChange={(e: any) => handlePerPageChange(Number(e.target.value))}
              style={{ fontSize: 12, fontWeight: 600, color: '#1e1e1e', fontFamily: 'Montserrat', border: '1px solid #d1d5db', borderRadius: 6, padding: '3px 4px', cursor: 'pointer', background: '#fff', outline: 'none' }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <span className="audit-pagination-text">
            {totalRecords === 0
              ? "No results"
              : `Showing ${showingFrom} to ${showingTo} of ${totalRecords} results`}
          </span>
        </div>
        <div className="audit-pagination-controls">
          <button
            type="button"
            className="audit-pagination-btn"
            disabled={currentPage <= 1}
            onClick={() => handlePageNav(currentPage - 1)}
          >
            Previous
          </button>
          {getPageNumbers(currentPage, totalPages).map((pageNum) => (
            <button
              key={pageNum}
              type="button"
              className={`audit-pagination-btn${pageNum === currentPage ? " active" : ""}`}
              onClick={() => handlePageNav(pageNum)}
            >
              {pageNum}
            </button>
          ))}
          <button
            type="button"
            className="audit-pagination-btn"
            disabled={currentPage >= totalPages}
            onClick={() => handlePageNav(currentPage + 1)}
          >
            Next
          </button>
        </div>
      </div>
      </div>

      {enableBulkDownloadModal && (
        <LocationAuditBulkDownloadModal
          onSubmit={() => setEnableBulkDownloadModal(false)}
          onCancel={() => setEnableBulkDownloadModal(false)}
          previousFilters={previousFilters}
        />
      )}
      {dropAudit && (
        <LocationDropAuditModal
          audit={dropAudit}
          onClose={() => setDropAudit(null)}
          onSuccess={() => {
            setDropAudit(null);
            refetch();
          }}
        />
      )}
      {(submitting || exporting || isFetching) && <PageLoader />}
    </div>
  );
}

export default AuditListV2;
